/**
 * salaryMass — masse salariale mensuelle avec salaires au-delà du 12e positionnés.
 *
 * Convention (alignée backend people_evolution_metrics._monthly_mass) :
 *   - chaque mois actif compte monthly_salary ;
 *   - chaque salaire au-delà de 12 (13e, 14e) vaut monthly_salary × mois actifs / 12,
 *     versé sur son mois de versement (cf. extraMonths / extraPayouts) — ou sur le
 *     dernier mois actif si le contrat se termine avant.
 *   Total annuel = monthly_salary × mois actifs × nb_of_salary/12.
 *
 * extraMonths : configuration [mois_13e, mois_2e_13e|null, mois_13e_du_14sal,
 *   mois_14e] (1..12). Construit via extraMonths(tenant) ; passée telle quelle
 *   aux fonctions ci-dessous. Deux blocs indépendants (13 sal. / 14 sal.).
 */

// Configuration des salaires extra d'un tenant :
//   [mois_13e (13 sal.), mois_2e_13e|null (scindé 50/50), mois_13e (14 sal.), mois_14e].
export function extraMonths(tenant) {
  const m13b = tenant?.thirteenth_salary_month_2;
  return [
    Number(tenant?.thirteenth_salary_month || 12),
    m13b ? Number(m13b) : null,
    Number(tenant?.fourteenth_salary_month_13 || 6),
    Number(tenant?.fourteenth_salary_month || 6),
  ];
}

// Échéancier des salaires au-delà du 12e : liste de [mois 1..12, fraction d'un
// salaire mensuel]. Miroir du backend people_evolution_metrics.extra_salary_payouts.
//  - 13 salaires : 13e en 1 fois (m13a) ou scindé 50/50 (m13a + m13b si défini).
//  - 14 salaires : 13e + 14e, un mois plein chacun, sur leurs propres mois (m14_13 + m14_14).
function extraPayouts(extras, [m13a, m13b, m14_13, m14_14]) {
  if (extras <= 0) return [];
  if (extras === 1) return m13b ? [[m13a, 0.5], [m13b, 0.5]] : [[m13a, 1]];
  return [[m14_13, 1], [m14_14, 1]];
}

export function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function contractActiveInMonth(c, year, monthIdx) {
  const monthStart = new Date(year, monthIdx, 1);
  const monthEnd   = new Date(year, monthIdx + 1, 0);
  if (!c.start_date) return false;
  const start = parseLocalDate(c.start_date);
  if (start > monthEnd) return false;
  if (c.end_date) {
    const end = parseLocalDate(c.end_date);
    if (end < monthStart) return false;
  }
  return true;
}

export function activeMonths(c, year) {
  const out = [];
  for (let m = 0; m < 12; m++) if (contractActiveInMonth(c, year, m)) out.push(m);
  return out;
}

// Versements supplémentaires (au-delà du 12e) d'un contrat sur l'année.
// months = config extraMonths (1..12). Liste de { month: index 0..11, amount }.
// Vide si nb_of_salary ≤ 12 ou inactif.
export function extraSalariesFor(c, year, months = [12, null, 6, 6]) {
  const extras = Math.max(0, Number(c.nb_of_salary || 12) - 12);
  if (extras === 0) return [];
  const actives = activeMonths(c, year);
  if (actives.length === 0) return [];
  const installment = Number(c.monthly_salary || 0) * actives.length / 12;
  const out = [];
  for (const [mx, frac] of extraPayouts(extras, months)) {
    const payIdx = mx - 1;
    const pay = actives.includes(payIdx) ? payIdx : actives[actives.length - 1];
    out.push({ month: pay, amount: installment * frac });
  }
  return out;
}

// Masse d'un contrat sur un mois donné (base + salaires extra éventuels).
export function contractMassInMonth(c, year, monthIdx, months = [12, null, 6, 6]) {
  let mass = contractActiveInMonth(c, year, monthIdx) ? Number(c.monthly_salary || 0) : 0;
  for (const t of extraSalariesFor(c, year, months)) if (t.month === monthIdx) mass += t.amount;
  return mass;
}

// Ajoute les salaires extra (13e, 14e) aux cellules mensuelles d'une personne
// ({ ..., gross }) : chaque versement = Σ gross payés / 12, posé sur son mois de
// versement — ou le dernier mois payé si la personne ne l'est plus (départ inclus).
export function addExtraSalariesToCells(cells, refContract, months = [12, null, 6, 6]) {
  const extras = refContract ? Math.max(0, Number(refContract.nb_of_salary || 12) - 12) : 0;
  if (extras === 0) return cells;
  const paid = cells.map(c => Number(c.gross || 0));
  const annual = paid.reduce((a, v) => a + v, 0);
  if (annual === 0) return cells;
  const installment = annual / 12;
  const lastPaid = (() => { for (let m = 11; m >= 0; m--) if (paid[m] !== 0) return m; return -1; })();
  const out = cells.map(c => ({ ...c }));
  for (const [mx, frac] of extraPayouts(extras, months)) {
    const payIdx = mx - 1;
    const pay = paid[payIdx] !== 0 ? payIdx : lastPaid;
    if (pay >= 0) out[pay].gross += installment * frac;
  }
  return out;
}

// Variante LISSÉE de addExtraSalariesToCells : même masse annuelle extra
// (13e/14e), mais RÉPARTIE à parts égales sur tous les mois actifs au lieu
// d'être concentrée sur le(s) mois de versement — pour un graphique de
// variation des salaires sans pic artificiel (toggle Concentré/Lissé,
// décision 2026-08-13).
export function smoothExtraSalariesToCells(cells, refContract, months = [12, null, 6, 6]) {
  const extras = refContract ? Math.max(0, Number(refContract.nb_of_salary || 12) - 12) : 0;
  if (extras === 0) return cells;
  const paid = cells.map(c => Number(c.gross || 0));
  const annual = paid.reduce((a, v) => a + v, 0);
  if (annual === 0) return cells;
  const installment = annual / 12;
  const totalExtra = extraPayouts(extras, months).reduce((s, [, frac]) => s + installment * frac, 0);
  const activeIdx = paid.map((v, i) => (v !== 0 ? i : -1)).filter(i => i >= 0);
  if (!activeIdx.length) return cells;
  const perMonth = totalExtra / activeIdx.length;
  const out = cells.map(c => ({ ...c }));
  for (const i of activeIdx) out[i].gross += perMonth;
  return out;
}

// Série 12 mois de la masse totale (tous contrats).
export function monthlyMassSeries(contracts, year, months = [12, null, 6, 6], annualized = false) {
  const out = Array(12).fill(0);
  for (const c of contracts) {
    // Annualisé : salaires extra lissés sur les mois actifs (× nb_of_salary/12) ;
    // sinon chaque salaire extra versé sur son mois de versement.
    const factor = annualized ? Number(c.nb_of_salary || 12) / 12 : 1;
    for (const m of activeMonths(c, year)) out[m] += Number(c.monthly_salary || 0) * factor;
    if (!annualized) {
      for (const t of extraSalariesFor(c, year, months)) out[t.month] += t.amount;
    }
  }
  return out;
}
